// ======================================================
// Updated by: Halimunnisa
// Feature: Customer Acceptance Workflow
// Purpose: Integrated Customer Acceptance tab into the main leads view.
// ======================================================
import React from 'react';
import { InitialQuotationScreen } from "../../../client_contact_stages/Initial-Quotation/screens/InitialQuotationScreen";
import { SiteVisitScreen } from "../../../client_contact_stages/Site-Visit/screens/SiteVisitScreen";
import { AppointmentScheduledScreen } from "../../../client_contact_stages/Appointment Scheduled/screens/AppointmentScheduledScreen";
import CustomerAcceptanceScreen from "../../../client_contact_stages/CustomerAcceptance";
import { LeadTableView } from "./LeadTableView";
import { LeadKanban } from "./LeadKanban";
import { LeadMediaViewer } from "./LeadMediaViewer";
import { leadService } from "../services/leadService";
import { showSuccessMessage, showApiError } from "@/lib/toast";
import { LEAD_STAGES } from "@/constants/globalConstants";

export const LeadsTabContent = ({
  view,
  filters,
  leads,
  leadsLoading,
  user,
  teamMembers,
  salesExecs,
  projectManagers,
  
  // Modals & State Setters
  setSelectedLead,
  setIsDetailOpen,
  setIsFormOpen,
  setIsAppointmentFormOpen,
  setIsDesignViewOpen,
  setDesignModalLead,
  setIsDesignFormOpen,
  setConfirmState,
  refetch,

  // Handlers
  toggleLeadStatus,
  handleUpdateLead,
  handleExport,
  isExporting
}) => {

  const isAll = filters.status === 'all';
  const isInitialQuotation = filters.status === 'initial_quotation';
  const isSiteVisit = filters.status === 'site_visit';
  const isAppointment = filters.status === 'appointment_scheduled';
  const isInitialQuotationApproval = filters.status === 'customer_acceptance';
  const isCustomerAcceptance = filters.status === 'cust_acceptance';
  
  const [viewerLead, setViewerLead] = React.useState(null);

  // Any status not matching the specific tabs will fall back to All leads view
  const isFallbackTab = !isAll && !isInitialQuotation && !isSiteVisit && !isAppointment && !isInitialQuotationApproval && !isCustomerAcceptance;
  const showAllView = isAll || isFallbackTab;

  return (
    <div className="min-h-[400px]">
      {/* Initial Quotation Tab */}
      {isInitialQuotation && view === "table" && (
        <div className="block">
          <InitialQuotationScreen
            filters={filters}
            leads={leads}
            isLoading={leadsLoading}
            refetchLeads={refetch}
            onLeadClick={(lead) => { setSelectedLead(lead); setIsDetailOpen(true); }}
          />
        </div>
      )}

      {/* Initial Quotation Approval Tab */}
      {isInitialQuotationApproval && view === "table" && (
        <InitialQuotationApproval leads={leads} isLoading={leadsLoading} onRefresh={refetch} teamMembers={teamMembers} isViewOnly={true} />
      )}

      {/* Customer Acceptance Tab */}
      {isCustomerAcceptance && view === "table" && (
        <CustomerAcceptanceScreen leads={leads} isLoading={leadsLoading} onRefresh={refetch} />
      )}

      {/* Site Visit Tab */}
      {isSiteVisit && view === "table" && (
        <SiteVisitScreen
          filters={filters}
          onLeadClick={(visit) => { setSelectedLead(visit); setIsDetailOpen(true); }}
        />
      )}

      {/* Appointment Scheduled Tab */}
      {isAppointment && view === "table" && (
        <AppointmentScheduledScreen 
          filters={filters}
          onLeadClick={(appt) => { setSelectedLead(appt); setIsDetailOpen(true); }}
          onExport={handleExport}
          isExporting={isExporting}
        />
      )}


      {/* All Leads (Table View) */}
      {showAllView && view === "table" && (
        <div className="block">
          <LeadTableView
            leads={leads}
            isLoading={leadsLoading}
            currentTab={filters.status}
            onLeadClick={(lead) => { setSelectedLead(lead); setIsDetailOpen(true); }}
            onEditClick={(lead) => { setSelectedLead(lead); setIsFormOpen(true); }}
            onMediaClick={(lead) => setViewerLead(lead)}
            onAddActivity={(leadId, note, type) => {
              // Activity handled here
            }}
            onToggleDisable={async (lead) => {
              const isEnabling = lead.isDisabled;
              setConfirmState({
                isOpen: true,
                title: isEnabling ? "Enable Lead" : "Disable Lead",
                message: `Are you sure you want to ${isEnabling ? 'enable' : 'disable'} this lead record? ${isEnabling ? 'This will make it visible in the active pipeline.' : 'This will move it to a restricted view and pause interactions.'}`,
                variant: isEnabling ? "info" : "danger",
                confirmText: isEnabling ? "Enable Record" : "Disable Record",
                onConfirm: async () => {
                  setConfirmState(prev => ({ ...prev, loading: true }));
                  try {
                    await leadService.toggleDisable(lead._id, !lead.isDisabled);
                    showSuccessMessage(`Lead ${isEnabling ? 'enabled' : 'disabled'} successfully`);
                    refetch();
                  } catch (err) {
                    showApiError(err);
                  } finally {
                    setConfirmState({ isOpen: false });
                  }
                }
              });
            }}
            updateStatus={toggleLeadStatus}
            onUpdateLead={handleUpdateLead}
            user={user}
            teamMembers={teamMembers}
            salesExecs={salesExecs}
            projectManagers={projectManagers}
          />
        </div>
      )}

      {/* All Leads (Kanban View) */}
      {showAllView && view !== "table" && (
        <div className="block">
          <LeadKanban
            stages={LEAD_STAGES}
            filtered={leads}
            isLoading={leadsLoading}
            onLeadClick={(lead) => { setSelectedLead(lead); setIsDetailOpen(true); }}
            updateStatus={toggleLeadStatus}
            onUpdateLead={handleUpdateLead}
            user={user}
            teamMembers={teamMembers}
          />
        </div>
      )}

      {/* Modals are managed in LeadsModals, but we can manage viewer here or there. Let's do it here for clarity. */}
      {viewerLead && (
        <LeadMediaViewer
          isOpen={!!viewerLead}
          onClose={() => setViewerLead(null)}
          lead={viewerLead}
        />
      )}
    </div>
  );
};
